import {
  FileImage,
  Image as ImageIcon,
  Layers,
  Palette,
  SlidersHorizontal,
  Square,
  Trash2,
} from "lucide-react";
import type { ReactNode } from "react";
import { gradientPresets, imagePresets } from "@/lib/constants";
import type {
  BackgroundType,
  BeautifierSettings,
  ExportFormat,
  PatternType,
  PostImageFit,
} from "@/lib/types";

type CustomizationPanelProps = {
  settings: BeautifierSettings;
  onChange: (updates: Partial<BeautifierSettings>) => void;
};

const backgroundTypes: Array<{
  id: BackgroundType;
  label: string;
  icon: typeof Palette;
}> = [
  { id: "solid", label: "Solid", icon: Palette },
  { id: "gradient", label: "Gradient", icon: Layers },
  { id: "pattern", label: "Pattern", icon: Square },
  { id: "image", label: "Image", icon: FileImage },
];

const patternTypes: Array<{ id: PatternType; label: string }> = [
  { id: "dots", label: "Dots" },
  { id: "grid", label: "Grid" },
  { id: "lines", label: "Lines" },
];

const exportFormats: Array<{ id: ExportFormat; label: string }> = [
  { id: "png", label: "PNG" },
  { id: "jpg", label: "JPG" },
];

const postImageFits: Array<{ id: PostImageFit; label: string }> = [
  { id: "fit", label: "Fit full" },
  { id: "fill", label: "Fill frame" },
  { id: "stretch", label: "Stretch" },
];

export function CustomizationPanel({
  settings,
  onChange,
}: CustomizationPanelProps) {
  const handleBackgroundUpload = (file: File | undefined) => {
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        onChange({
          customBackgroundImage: reader.result,
          backgroundType: "image",
        });
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <aside className="rounded-lg border border-slate-200 bg-white p-5 shadow-panel">
      <div className="flex items-center gap-2">
        <SlidersHorizontal className="h-5 w-5 text-ocean" aria-hidden="true" />
        <h3 className="text-lg font-semibold text-ink">Design controls</h3>
      </div>

      <div className="mt-6 space-y-7">
        <fieldset>
          <legend className="text-sm font-semibold text-slate-700">
            Background type
          </legend>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {backgroundTypes.map((type) => {
              const Icon = type.icon;
              const isActive = settings.backgroundType === type.id;

              return (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => onChange({ backgroundType: type.id })}
                  className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                    isActive
                      ? "border-ocean bg-ocean text-white"
                      : "border-slate-200 bg-slate-50 text-ink hover:bg-slate-100"
                  }`}
                  aria-pressed={isActive}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {type.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <ControlGroup title="Background color">
          <ColorField
            label="Background color"
            value={settings.backgroundColor}
            onChange={(value) => onChange({ backgroundColor: value })}
          />
        </ControlGroup>

        <ControlGroup title="Gradient presets">
          <div className="grid grid-cols-2 gap-2">
            {gradientPresets.map((preset) => {
              const isActive = settings.gradientPreset === preset.id;

              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() =>
                    onChange({
                      gradientPreset: preset.id,
                      backgroundType: "gradient",
                    })
                  }
                  className={`rounded-lg border p-2 text-left transition ${
                    isActive
                      ? "border-ocean bg-sky-50"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                  aria-pressed={isActive}
                >
                  <span
                    className="block h-8 rounded-md"
                    style={{ backgroundImage: preset.value }}
                    aria-hidden="true"
                  />
                  <span className="mt-2 block text-sm font-semibold text-ink">
                    {preset.label}
                  </span>
                </button>
              );
            })}
          </div>
        </ControlGroup>

        <ControlGroup title="Pattern">
          <div className="grid grid-cols-3 gap-2">
            {patternTypes.map((pattern) => {
              const isActive = settings.patternType === pattern.id;

              return (
                <button
                  key={pattern.id}
                  type="button"
                  onClick={() =>
                    onChange({
                      patternType: pattern.id,
                      backgroundType: "pattern",
                    })
                  }
                  className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                    isActive
                      ? "border-ocean bg-ocean text-white"
                      : "border-slate-200 bg-slate-50 text-ink hover:bg-slate-100"
                  }`}
                  aria-pressed={isActive}
                >
                  {pattern.label}
                </button>
              );
            })}
          </div>
        </ControlGroup>

        <ControlGroup title="Background image">
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm font-semibold text-ink transition hover:bg-slate-100">
              <ImageIcon className="h-4 w-4" aria-hidden="true" />
              Upload
              <input
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(event) =>
                  handleBackgroundUpload(event.target.files?.[0])
                }
              />
            </label>
            <button
              type="button"
              onClick={() => onChange({ customBackgroundImage: "" })}
              disabled={!settings.customBackgroundImage}
              className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-ink transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Clear
            </button>
          </div>
        </ControlGroup>

        <ControlGroup title="Card colors">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <ColorField
              label="Card background"
              value={settings.cardBackgroundColor}
              onChange={(value) => onChange({ cardBackgroundColor: value })}
            />
            <ColorField
              label="Text color"
              value={settings.textColor}
              onChange={(value) => onChange({ textColor: value })}
            />
          </div>
        </ControlGroup>

        <ControlGroup title="Layout controls">
          <Slider
            label="Card opacity"
            min={50}
            max={100}
            value={settings.cardOpacity}
            suffix="%"
            onChange={(value) => onChange({ cardOpacity: value })}
          />
          <Slider
            label="Padding"
            min={28}
            max={140}
            value={settings.padding}
            suffix="px"
            onChange={(value) => onChange({ padding: value })}
          />
          <Slider
            label="Border radius"
            min={0}
            max={64}
            value={settings.borderRadius}
            suffix="px"
            onChange={(value) => onChange({ borderRadius: value })}
          />
          <Slider
            label="Shadow intensity"
            min={0}
            max={80}
            value={settings.shadowIntensity}
            suffix=""
            onChange={(value) => onChange({ shadowIntensity: value })}
          />
        </ControlGroup>

        <ControlGroup title="Post image display">
          <div className="grid grid-cols-3 gap-2">
            {postImageFits.map((fit) => {
              const isActive = settings.postImageFit === fit.id;

              return (
                <button
                  key={fit.id}
                  type="button"
                  onClick={() => onChange({ postImageFit: fit.id })}
                  className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                    isActive
                      ? "border-ocean bg-ocean text-white"
                      : "border-slate-200 bg-slate-50 text-ink hover:bg-slate-100"
                  }`}
                  aria-pressed={isActive}
                >
                  {fit.label}
                </button>
              );
            })}
          </div>
          <Slider
            label="Image area"
            min={24}
            max={68}
            value={settings.postImageHeight}
            suffix="%"
            onChange={(value) => onChange({ postImageHeight: value })}
          />
        </ControlGroup>

        <ControlGroup title="Image size">
          <label className="sr-only" htmlFor="image-preset">
            Image size preset
          </label>
          <select
            id="image-preset"
            value={settings.imagePresetId}
            onChange={(event) => onChange({ imagePresetId: event.target.value })}
            className="min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-ink shadow-sm"
          >
            {imagePresets.map((preset) => (
              <option key={preset.id} value={preset.id}>
                {preset.label}
              </option>
            ))}
          </select>
        </ControlGroup>

        <ControlGroup title="Export settings">
          <div className="grid grid-cols-2 gap-2">
            {exportFormats.map((format) => {
              const isActive = settings.exportFormat === format.id;

              return (
                <button
                  key={format.id}
                  type="button"
                  onClick={() => onChange({ exportFormat: format.id })}
                  className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                    isActive
                      ? "border-ink bg-ink text-white"
                      : "border-slate-200 bg-slate-50 text-ink hover:bg-slate-100"
                  }`}
                  aria-pressed={isActive}
                >
                  {format.label}
                </button>
              );
            })}
          </div>
          {settings.exportFormat === "jpg" ? (
            <div className="mt-4">
              <Slider
                label="JPG quality"
                min={60}
                max={100}
                value={settings.jpgQuality}
                suffix="%"
                onChange={(value) => onChange({ jpgQuality: value })}
              />
            </div>
          ) : null}
        </ControlGroup>
      </div>
    </aside>
  );
}

function ControlGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section>
      <h4 className="text-sm font-semibold text-slate-700">{title}</h4>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex min-h-11 items-center gap-3 rounded-lg border border-slate-300 bg-white px-3 shadow-sm">
      <input
        type="color"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-7 w-8 cursor-pointer rounded border-0 bg-transparent p-0"
        aria-label={label}
      />
      <span className="min-w-0 flex-1 text-sm font-medium text-slate-700">
        {label}
      </span>
      <span className="text-xs font-semibold uppercase text-slate-500">
        {value}
      </span>
    </label>
  );
}

function Slider({
  label,
  min,
  max,
  value,
  suffix,
  onChange,
}: {
  label: string;
  min: number;
  max: number;
  value: number;
  suffix: string;
  onChange: (value: number) => void;
}) {
  return (
    <label className="mt-4 block first:mt-0">
      <span className="flex items-center justify-between gap-4 text-sm font-medium text-slate-700">
        {label}
        <span className="font-semibold text-ink">
          {value}
          {suffix}
        </span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="mt-2 h-2 w-full cursor-pointer accent-ocean"
      />
    </label>
  );
}
